/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import DashboardLayout from "../../../Layout/DashboardLayout";
import { monthlist } from "../../../utils/constant";
import styles from "../../../styles/social/dashboard.module.css";
import Cards from "../../../Components/Social/Cards";
import PieChart from "../../../Components/GraphNCharts/Pie";
import BarChart from "../../../Components/GraphNCharts/BarChart";
import LineChart from "../../../Components/GraphNCharts/LineChart";
import { Doughnut } from "react-chartjs-2";
import { get } from "../../../utils/api";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import MainSidebar from "../../../Layout/MainSidebar";
import SocialLayout from "../../../Layout/SocialLayout";
import { useUser } from "../../../context/AuthContext";
import Unauthorized from "../../../Components/Unauthorized/Unauthorized";
import {
  truncateNumber,
  formatNumberCommaSeparated,
} from "../../../utils/common";
import GroupAdminSidebar from "../../../Layout/GroupAdminSidebar";
import useAuthStore from "../../../store/ContextUser";
import { CircularProgress, Box, TextField } from "@mui/material";
const getChangeType = (percentage) =>
  percentage > 0 ? "increase" : "decrease";

const getPeriodCode = (year, month) =>
  (Number(year) || 0) * 100 + (Number(month) || 0);

const getAverage = (values = []) => {
  const nums = values.map((v) => Number(v)).filter((v) => !Number.isNaN(v));
  if (!nums.length) return 0;
  return nums.reduce((acc, val) => acc + val, 0) / nums.length;
};

const renderNoData = (height = "260px") => (
  <div
    style={{
      height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#9aa0a6",
      fontSize: "20px",
      fontWeight: 600,
    }}
  >
    No Data Found
  </div>
);

const toAlphaLabel = (index) => {
  let n = Number(index) + 1;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

const SocialDashboard = () => {
  const { user, module } = useUser();
  const { selectedCompany } = useAuthStore();
  let currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const [selectedFacility, setSelectedFacility] = useState([]);
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });

  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  let hasAccess =
    user?.companyAdmin ||
    user?.facilityManger ||
    module?.includes("Social Dashboard");

  // View mode: "monthly" (first design) or "annual" (second design)
  const [viewMode, setViewMode] = useState("monthly");
  const [socialData, setSocialData] = useState({
    facility: {},
    labour: {},
    totalWorkforce: {},
    humanRights: {},
    training: {},
    customerSatisfaction: {},
  });
  const [isLoadingWorkforce, setIsLoadingWorkforce] = useState(false);

  const yearList = useMemo(() => {
    const startYear = 2021;
    const currentYear = new Date().getFullYear();
    return Array.from(
      { length: currentYear - startYear + 1 },
      (_, i) => startYear + i,
    );
  }, []);

  const monthlyRefsInRange = useMemo(() => {
    const refs = Array.isArray(socialData?.totalWorkforce?.socialDataRefs)
      ? socialData.totalWorkforce.socialDataRefs
      : [];

    const startCode = getPeriodCode(fromDate?.year, fromDate?.month);
    const endCode = getPeriodCode(toDate?.year, toDate?.month);

    return refs.filter((ref) => {
      const monthlyData = ref?.monthlyData || {};
      const refMonth =
        Number(monthlyData?.monthToSubmit) ||
        Number(ref?.monthToSubmit) ||
        Number(monthlyData?.month) ||
        Number(ref?.month);
      const refYear =
        Number(monthlyData?.yearToSubmit) ||
        Number(ref?.yearToSubmit) ||
        Number(monthlyData?.year) ||
        Number(ref?.year);
      if (!refMonth || !refYear) return false;
      const code = getPeriodCode(refYear, refMonth);
      return code >= startCode && code <= endCode;
    });
  }, [socialData?.totalWorkforce?.socialDataRefs, fromDate, toDate]);

  const annualRowsInRange = useMemo(() => {
    const annualArr = Array.isArray(socialData?.totalWorkforce?.data?.annual)
      ? socialData.totalWorkforce.data.annual
      : [];
    const startYear = Number(fromDate?.year) || 0;
    const endYear = Number(toDate?.year) || 0;

    return annualArr.filter((row) => {
      const yearVal =
        Number(row?.yearToSubmit) ||
        Number(row?.year) ||
        Number(row?.data?.yearToSubmit) ||
        Number(row?.data?.year);
      if (!yearVal) return true;
      return yearVal >= startYear && yearVal <= endYear;
    });
  }, [socialData?.totalWorkforce?.data?.annual, fromDate?.year, toDate?.year]);

  // Labour Practices (dashboard card) - average across selected monthly range
  const labourPracticesSummary = useMemo(() => {
    if (monthlyRefsInRange.length > 0) {
      const lpRows = monthlyRefsInRange
        .map((r) => r?.monthlyData?.labourPractices || null)
        .filter(Boolean);
      console.log(lpRows, "lpRows");
      return {
        labourComplaints: getAverage(
          lpRows.map((lp) => lp?.labourComplaints || 0),
        ),
        labourComplaintsResolved: getAverage(
          lpRows.map((lp) => lp?.labourComplaintsResolved || 0),
        ),
        labourComplaintsPending: getAverage(
          lpRows.map((lp) => lp?.labourComplaintsPending || 0),
        ),
        poshResolved: getAverage(lpRows.map((lp) => lp?.POSH?.filed || 0)),
        poshPending: getAverage(lpRows.map((lp) => lp?.POSH?.pending || 0)),
        complaintRate: getAverage(
          lpRows.map((lp) => lp?.labourComplaintsResolutionRate || 0),
        ),
      };
    }

    return {
      labourComplaints: 0,
      labourComplaintsResolved: 0,
      labourComplaintsPending: 0,
      poshResolved: 0,
      poshPending: 0,
      complaintRate: 0,
    };
  }, [monthlyRefsInRange]);

  // Human Rights - average across selected monthly range
  const humanRightsSummary = useMemo(() => {
    if (monthlyRefsInRange.length > 0) {
      const hrRows = monthlyRefsInRange
        .map((r) => r?.monthlyData?.humanRights || null)
        .filter(Boolean);

      return {
        incidentsResolutionRate: getAverage(
          hrRows.map((hr) => hr?.incidentsResolutionRate || 0),
        ),
      };
    }

    return {
      incidentsResolutionRate: 0,
    };
  }, [monthlyRefsInRange]);

  // Health & Safety - average across selected monthly range
  const healthAndSafetySummary = useMemo(() => {
    if (monthlyRefsInRange.length > 0) {
      const hsRows = monthlyRefsInRange
        .map((r) => r?.monthlyData?.healthAndSafety || null)
        .filter(Boolean);
      return {
        incidentCount: getAverage(hsRows.map((hs) => hs?.incidentCount || 0)),
      };
    }
    return { incidentCount: 0 };
  }, [monthlyRefsInRange]);

  // Customer Satisfaction - average across selected monthly range
  const customerSatisfactionSummary = useMemo(() => {
    if (monthlyRefsInRange.length > 0) {
      const csRows = monthlyRefsInRange
        .map((r) => r?.monthlyData?.customerSatisfaction || null)
        .filter(Boolean);
      return {
        dataBreaches: getAverage(csRows.map((cs) => cs?.dataBreachRate ?? 0)),
        productRecalls: getAverage(
          csRows.map(
            (cs) =>
              cs?.productRecallRate ??
              cs?.productRecalls ??
              cs?.productRecall ??
              cs?.productRecallCount ??
              0,
          ),
        ),
        marketingComplaints: getAverage(
          csRows.map((cs) => cs?.marketingComplaints ?? 0),
        ),
      };
    }
    return {
      dataBreaches: 0,
      productRecalls: 0,
      marketingComplaints: 0,
    };
  }, [monthlyRefsInRange]);

  // Training and Development - average budget amount across selected monthly range
  const trainingAndDevelopmentSummary = useMemo(() => {
    if (monthlyRefsInRange.length > 0) {
      const tRows = monthlyRefsInRange
        .map((r) => r?.monthlyData?.trainingAndDevelopment || null)
        .filter(Boolean);
      const amounts = tRows.map((tAndD) => tAndD?.budgetAllocated?.amount || 0);
      const firstCurrency =
        tRows.find((tAndD) => tAndD?.budgetAllocated?.currency)?.budgetAllocated
          ?.currency || "";

      return {
        currency: firstCurrency,
        amount: getAverage(amounts),
      };
    }

    return {
      currency: "",
      amount: "",
    };
  }, [monthlyRefsInRange]);

  const trainingBudgetDisplay = useMemo(() => {
    if (monthlyRefsInRange.length > 0) {
      const budgets = monthlyRefsInRange
        .map(
          (ref) =>
            ref?.monthlyData?.trainingAndDevelopment?.budgetAllocated || null,
        )
        .filter(Boolean)
        .map((item) => ({
          currency: String(item?.currency || "")
            .toUpperCase()
            .trim(),
          amount: Number(item?.amount || 0),
        }))
        .filter((item) => item.currency && !Number.isNaN(item.amount));

      if (budgets.length > 0) {
        const currencies = [...new Set(budgets.map((item) => item.currency))];
        if (currencies.length === 1) {
          return `${truncateNumber(getAverage(budgets.map((item) => item.amount)), 2)} ${currencies[0]}`;
        }

        const grouped = currencies.map((currency) => {
          const amounts = budgets
            .filter((item) => item.currency === currency)
            .map((item) => truncateNumber(item.amount, 2));
          return `${currency}: ${amounts.join(", ")}`;
        });
        return grouped.join(" | ");
      }
    }

    const fallbackCurrency = String(
      trainingAndDevelopmentSummary?.currency || "",
    )
      .toUpperCase()
      .trim();
    const fallbackAmount = Number(trainingAndDevelopmentSummary?.amount || 0);
    return fallbackCurrency
      ? `${truncateNumber(fallbackAmount, 2)} ${fallbackCurrency}`
      : truncateNumber(fallbackAmount, 2);
  }, [monthlyRefsInRange, trainingAndDevelopmentSummary]);

  const communitySpendDisplay = useMemo(() => {
    if (monthlyRefsInRange.length > 0) {
      const donations = monthlyRefsInRange
        .map(
          (ref) => ref?.monthlyData?.communityEngagement?.totalDonated || null,
        )
        .filter(Boolean)
        .map((item) => ({
          currency: String(item?.currency || "")
            .toUpperCase()
            .trim(),
          amount: Number(item?.amount || 0),
        }))
        .filter((item) => item.currency && !Number.isNaN(item.amount));

      if (donations.length > 0) {
        const allInr = donations.every((item) => item.currency === "INR");
        if (allInr) {
          const inrTotal = donations.reduce(
            (acc, item) => acc + item.amount,
            0,
          );
          return `${truncateNumber(inrTotal, 0)} INR`;
        }
        return donations
          .map((item) => `${truncateNumber(item.amount, 0)} ${item.currency}`)
          .join(" , ");
      }
    }

    const fallback =
      socialData?.totalWorkforce?.communityEngagement?.totalDonated || {};
    const fallbackCurrency = String(fallback?.currency || "")
      .toUpperCase()
      .trim();
    const fallbackAmount = Number(fallback?.amount || 0);

    if (fallbackCurrency) {
      return `${truncateNumber(fallbackAmount, 0)} ${fallbackCurrency}`;
    }
    return truncateNumber(fallbackAmount, 0);
  }, [
    monthlyRefsInRange,
    socialData?.totalWorkforce?.communityEngagement?.totalDonated,
  ]);

  const volunteerHoursAverage = useMemo(() => {
    if (monthlyRefsInRange.length > 0) {
      return getAverage(
        monthlyRefsInRange.map(
          (ref) => ref?.monthlyData?.communityEngagement?.volunteerHours || 0,
        ),
      );
    }
    return Number(
      socialData?.totalWorkforce?.communityEngagement?.volunteerHours || 0,
    );
  }, [
    monthlyRefsInRange,
    socialData?.totalWorkforce?.communityEngagement?.volunteerHours,
  ]);

  // Employee Wellbeing - average across annual records in selected range
  const employeeWellBeingSummary = useMemo(() => {
    const ewbRows = annualRowsInRange
      .map((annual) => annual?.employeeWellBeing || null)
      .filter(Boolean);

    return {
      turnoverRatePermEmpMale: getAverage(
        ewbRows.map((ewb) => ewb?.turnoverRatePermEmpMale || 0),
      ),
      turnoverRatePermEmpFemale: getAverage(
        ewbRows.map((ewb) => ewb?.turnoverRatePermEmpFemale || 0),
      ),
      turnoverRatePermWorkerMale: getAverage(
        ewbRows.map((ewb) => ewb?.turnoverRatePermWorkerMale || 0),
      ),
      turnoverRatePermWorkerFemale: getAverage(
        ewbRows.map((ewb) => ewb?.turnoverRatePermWorkerFemale || 0),
      ),
    };
  }, [annualRowsInRange]);

  const annualBenefitsTotalInrAverage = useMemo(() => {
    if (!annualRowsInRange.length) return 0;
    const perYearTotals = annualRowsInRange.map((annual) => {
      const programs = Array.isArray(
        annual?.employeeBenefits?.benefitsProgramsOffered,
      )
        ? annual.employeeBenefits.benefitsProgramsOffered
        : [];
      return programs.reduce(
        (acc, p) =>
          acc +
          (Number(p?.benefitsInINRPermanent) || 0) +
          (Number(p?.benefitsInINROther) || 0),
        0,
      );
    });
    return getAverage(perYearTotals);
  }, [annualRowsInRange]);

  const annualAssessmentSummary = useMemo(() => {
    const bucket = {};

    annualRowsInRange.forEach((annual) => {
      const assessments = Array.isArray(annual?.labourPractices?.assessments)
        ? annual.labourPractices.assessments
        : [];

      assessments.forEach((a) => {
        const key = a?.assessmentType || a?.type || "Assessment";
        if (!bucket[key]) {
          bucket[key] = { label: key, sum: 0, count: 0 };
        }
        bucket[key].sum += Number(a?.percentageAssessed) || 0;
        bucket[key].count += 1;
      });
    });

    const labels = Object.keys(bucket);
    const values = labels.map((k) =>
      bucket[k].count > 0 ? bucket[k].sum / bucket[k].count : 0,
    );

    return { labels, values };
  }, [annualRowsInRange]);

  const annualPolicyCoverageSummary = useMemo(() => {
    if (!annualRowsInRange.length) {
      return {
        availableAvg: 0,
        totalAvg: 0,
        coveragePercent: 0,
        remainingPercent: 100,
      };
    }
    console.log("annualRowsInRange", annualRowsInRange);
    const policyCounts = annualRowsInRange.map((annual) => {
      const di = annual?.diversityAndInclusion || {};
      const ce = annual?.communityEngagement || {};
      const lp = annual?.labourPractices || {};
      const hs = annual?.healthAndSafety || {};
      const hr = annual?.humanRights || {};
      const eb = annual?.employeeBenefits || {};

      const policies = [
        di.equalOpportunityPolicy,
        di.genderBasedViolencePolicy,
        ce.CSR,
        eb.benefitsCoveredInHandbook,
        lp.collectiveBargainingAgreements,
        lp.freedomOfAssociation,
        lp.labourLawCompliance,
        lp.legalMinimumWageCompliance,
        lp.noChildLabourPolicy,
        lp.noForcedLabourPolicy,
        hs.healthAndSafetyPolicy,
        hr.humanRightsPolicy,
      ].filter(Boolean);

      const totalListedPolicyCount = policies.length;
      const availableWithCompany = policies.filter(
        (p) => p?.status === true || p?.status === "true",
      ).length;

      return { totalListedPolicyCount, availableWithCompany };
    });

    const availableAvg = getAverage(
      policyCounts.map((x) => x.availableWithCompany || 0),
    );
    const totalAvg = getAverage(
      policyCounts.map((x) => x.totalListedPolicyCount || 0),
    );
    const coveragePercent =
      totalAvg > 0 ? Math.min((availableAvg / totalAvg) * 100, 100) : 0;
    const remainingPercent = Math.max(100 - coveragePercent, 0);

    return { availableAvg, totalAvg, coveragePercent, remainingPercent };
  }, [annualRowsInRange]);

  const annualEmployeeBenefitsSummary = useMemo(() => {
    if (!annualRowsInRange.length) {
      return { labels: [], permanentByProgram: [], otherByProgram: [] };
    }

    const byProgram = {};

    annualRowsInRange.forEach((annual) => {
      const programs = Array.isArray(
        annual?.employeeBenefits?.benefitsProgramsOffered,
      )
        ? annual.employeeBenefits.benefitsProgramsOffered
        : [];

      programs.forEach((p) => {
        const rawName = String(p?.benefit || "").trim();
        const key = rawName || "Program";
        const lname = rawName.toLowerCase();
        const isMaternity =
          lname === "maternity benefits" || lname === "maternity";
        const isPaternity =
          lname === "paternity benefits" || lname === "paternity";
        if (!byProgram[key]) {
          byProgram[key] = {
            label: key,
            permanentSum: 0,
            permanentCount: 0,
            otherSum: 0,
            otherCount: 0,
          };
        }

        const permMale = Number(p?.permanentMale) || 0;
        const permFemale = Number(p?.permanentFemale) || 0;
        const otherMale = Number(p?.otherMale) || 0;
        const otherFemale = Number(p?.otherFemale) || 0;

        const effectivePermMale = isMaternity ? 0 : permMale;
        const effectivePermFemale = isPaternity ? 0 : permFemale;
        const effectiveOtherMale = isMaternity ? 0 : otherMale;
        const effectiveOtherFemale = isPaternity ? 0 : otherFemale;

        byProgram[key].permanentSum += effectivePermMale + effectivePermFemale;
        byProgram[key].permanentCount += 1;

        byProgram[key].otherSum += effectiveOtherMale + effectiveOtherFemale;
        byProgram[key].otherCount += 1;
      });
    });

    const labels = Object.keys(byProgram);
    const permanentByProgram = labels.map((label) => {
      const row = byProgram[label];
      return row.permanentCount > 0 ? row.permanentSum / row.permanentCount : 0;
    });
    const otherByProgram = labels.map((label) => {
      const row = byProgram[label];
      return row.otherCount > 0 ? row.otherSum / row.otherCount : 0;
    });

    return { labels, permanentByProgram, otherByProgram };
  }, [annualRowsInRange]);

  const averagedAwarenessTrainingsData = useMemo(() => {
    if (monthlyRefsInRange.length > 0) {
      const bucket = {};

      monthlyRefsInRange.forEach((ref) => {
        const tAndD =
          ref?.monthlyData?.trainingAndDevelopment ||
          ref?.monthlyData?.TrainingAndDevelopment ||
          {};

        const awarenessRows = Array.isArray(
          tAndD?.awarenessTrainings?.trainings,
        )
          ? tAndD.awarenessTrainings.trainings
          : Array.isArray(tAndD?.awarenessTrainings)
            ? tAndD.awarenessTrainings
            : [];

        awarenessRows.forEach((item) => {
          const topic = item?.topic || "Topic";
          if (!bucket[topic]) {
            bucket[topic] = { topic, sum: 0, count: 0 };
          }
          bucket[topic].sum += Number(item?.percentage) || 0;
          bucket[topic].count += 1;
        });
      });

      return Object.values(bucket).map((row) => ({
        topic: row.topic,
        percentage: row.count > 0 ? row.sum / row.count : 0,
      }));
    }

    return socialData?.totalWorkforce?.awarenessTrainingsData || [];
  }, [monthlyRefsInRange, socialData?.totalWorkforce?.awarenessTrainingsData]);

  const skillTrainingTopics = useMemo(
    () =>
      socialData?.totalWorkforce?.skillTrainingsData?.map((item) => item.topic) || [],
    [socialData?.totalWorkforce?.skillTrainingsData],
  );
  const awarenessTrainingTopics = useMemo(
    () => averagedAwarenessTrainingsData?.map((item) => item.topic) || [],
    [averagedAwarenessTrainingsData],
  );
  const skillTrainingAlphaLabels = useMemo(
    () => skillTrainingTopics.map((_, idx) => toAlphaLabel(idx)),
    [skillTrainingTopics],
  );
  const awarenessTrainingAlphaLabels = useMemo(
    () => awarenessTrainingTopics.map((_, idx) => toAlphaLabel(idx)),
    [awarenessTrainingTopics],
  );

  const toYearOptions = useMemo(
    () => yearList.filter((year) => Number(year) >= Number(fromDate.year)),
    [yearList, fromDate.year],
  );

  const fromMonthOptions = useMemo(() => {
    // Prevent selecting future month in current year
    if (Number(fromDate.year) === Number(currentYear)) {
      return monthlist.filter((m) => Number(m.id) <= Number(currentMonth));
    }
    return monthlist;
  }, [fromDate.year, currentYear, currentMonth]);

  const toMonthOptions = useMemo(() => {
    let options =
      Number(toDate.year) > Number(fromDate.year)
        ? monthlist
        : monthlist.filter((m) => Number(m.id) >= Number(fromDate.month));

    // Prevent selecting future month in current year
    if (Number(toDate.year) === Number(currentYear)) {
      options = options.filter((m) => Number(m.id) <= Number(currentMonth));
    }

    return options;
  }, [fromDate.year, fromDate.month, toDate.year, currentYear, currentMonth]);

  useEffect(() => {
    // Keep From month valid when user switches to current year
    if (
      Number(fromDate.year) === Number(currentYear) &&
      Number(fromDate.month) > Number(currentMonth)
    ) {
      setFromDate((prev) => ({ ...prev, month: currentMonth }));
    }
  }, [fromDate.year, fromDate.month, currentYear, currentMonth]);

  useEffect(() => {
    // Keep To month valid for range + future-month rules
    const maxAllowedMonth =
      Number(toDate.year) === Number(currentYear) ? Number(currentMonth) : 12;
    const minAllowedMonth =
      Number(toDate.year) === Number(fromDate.year)
        ? Number(fromDate.month)
        : 1;

    if (Number(toDate.month) > maxAllowedMonth) {
      setToDate((prev) => ({ ...prev, month: maxAllowedMonth }));
      return;
    }
    if (Number(toDate.month) < minAllowedMonth) {
      setToDate((prev) => ({ ...prev, month: minAllowedMonth }));
    }
  }, [
    toDate.year,
    toDate.month,
    fromDate.year,
    fromDate.month,
    currentYear,
    currentMonth,
  ]);

  // Diversity in Leadership - average across selected monthly range
  const diversityInLeadershipData = useMemo(() => {
    const byMonthDetailed =
      socialData?.totalWorkforce?.data?.monthly?.byMonthDetailed || [];
    const startCode = getPeriodCode(fromDate?.year, fromDate?.month);
    const endCode = getPeriodCode(toDate?.year, toDate?.month);

    const rowsInRange = byMonthDetailed.filter((row) => {
      const code = getPeriodCode(row?.year, row?.month);
      return code >= startCode && code <= endCode;
    });

    const maleAvg = getAverage(
      rowsInRange.map(
        (row) => Number(row?.diversityInLeadership?.malePercentage) || 0,
      ),
    );
    const femaleAvg = getAverage(
      rowsInRange.map(
        (row) => Number(row?.diversityInLeadership?.femalePercentage) || 0,
      ),
    );
    const othersAvg = getAverage(
      rowsInRange.map(
        (row) => Number(row?.diversityInLeadership?.othersPercentage) || 0,
      ),
    );

    const maleValue =
      rowsInRange.length > 0
        ? maleAvg
        : Number(socialData?.facility?.diversityInLeadership?.male ?? 80);
    const femaleValue =
      rowsInRange.length > 0
        ? femaleAvg
        : Number(socialData?.facility?.diversityInLeadership?.female ?? 20);
    const othersValue =
      rowsInRange.length > 0
        ? othersAvg
        : Number(socialData?.facility?.diversityInLeadership?.others ?? 0);

    const hasData = maleValue > 0 || femaleValue > 0 || othersValue > 0;
    const finalMale = hasData ? maleValue : 80;
    const finalFemale = hasData ? femaleValue : 20;

    const data = [finalMale, finalFemale];
    const colors = ["#FF4C4C", "#007BFF"];
    const labels = ["Male", "Female"];

    // Only include "Others" if value > 0
    if (othersValue > 0) {
      data.push(othersValue);
      colors.push("#eb7e0e");
      labels.push("Others");
    }

    return {
      datasets: [
        {
          data: data,
          backgroundColor: colors,
          borderColor: "#fff",
          borderWidth: 0,
          offset: [0, 15],
        },
      ],
      labels: labels,
      malePercentage: finalMale,
      femalePercentage: finalFemale,
      othersPercentage: othersValue,
    };
  }, [
    socialData?.totalWorkforce?.data?.monthly?.byMonthDetailed,
    socialData?.facility?.diversityInLeadership,
    fromDate,
    toDate,
  ]);

  const customDatasets = useMemo(() => {
    if (
      diversityInLeadershipData?.datasets &&
      diversityInLeadershipData.datasets.length > 0
    ) {
      return diversityInLeadershipData.datasets;
    }
    // Default fallback data to ensure pie chart always renders
    return [
      {
        data: [80, 20],
        backgroundColor: ["#FF4C4C", "#007BFF"],
        borderColor: "#fff",
        borderWidth: 0,
        offset: [0, 15],
      },
    ];
  }, [diversityInLeadershipData]);
  const DiversityinLeadershipDatasets = [
    {
      data: [
        socialData?.facility?.genderDiversity?.male ?? 0,
        socialData?.facility?.genderDiversity?.female ?? 0,
        socialData?.facility?.genderDiversity?.others ?? 0,
      ],
      backgroundColor: ["#FF4C4C", "#007BFF", "#eb7e0e"],
      borderColor: "#fff",
      borderWidth: 0,
      offset: [0, 15],
    },
  ];

  // Labels for Diversity in Leadership display - using data from byMonthDetailed
  const labels = useMemo(() => {
    const malePercentage = diversityInLeadershipData?.malePercentage ?? 80;
    const femalePercentage = diversityInLeadershipData?.femalePercentage ?? 20;
    const othersPercentage = diversityInLeadershipData?.othersPercentage ?? 0;

    return [
      {
        color: "#FF4C4C",
        label: "Male",
        value: `${truncateNumber(malePercentage, 2)}%`,
      },
      {
        color: "#007BFF",
        label: "Female",
        value: `${truncateNumber(femalePercentage, 2)}%`,
      },
      ...(othersPercentage > 0
        ? [
            {
              color: "#eb7e0e",
              label: "Others",
              value: `${truncateNumber(othersPercentage, 2)}%`,
            },
          ]
        : []),
    ];
  }, [diversityInLeadershipData]);
  const GenderDiversitylabels = [
    {
      color: "#FF4C4C",
      label: "Male",
      value: `${socialData?.facility?.genderDiversity?.male ?? 0}%`,
    },
    {
      color: "#007BFF",
      label: "Female",
      value: `${socialData?.facility?.genderDiversity?.female ?? 0}%`,
    },
    {
      color: "#eb7e0e",
      label: "Others",
      value: `${socialData?.facility?.genderDiversity?.others ?? 0}%`,
    },
  ];

  // Total Workforce data - check both old and new API structure
  const permanentWorkforce =
    socialData?.totalWorkforce?.permanent ??
    socialData?.facility?.totalWorkforce?.permanent ??
    50;
  const temporaryWorkforce =
    socialData?.totalWorkforce?.temporary ??
    socialData?.facility?.totalWorkforce?.temporary ??
    10;
  const totalWorkforce = permanentWorkforce + temporaryWorkforce;

  // Generate monthly labels and data based on selected date range.
  // Always include all selected months; if API has no row for a month, use zeros.
  const monthlyWorkforceData = useMemo(() => {
    // Generate month labels from fromDate to toDate
    const monthLabels = [];
    const monthData = [];

    let currentMonth = fromDate.month;
    let currentYear = fromDate.year;
    const endMonth = toDate.month;
    const endYear = toDate.year;

    // Get monthly data from API - structure: totalWorkforce.monthlyData.byMonthDetailed (array)
    const byMonthDetailed =
      socialData?.totalWorkforce?.data?.monthly?.byMonthDetailed || [];
    while (
      currentYear < endYear ||
      (currentYear === endYear && currentMonth <= endMonth)
    ) {
      const monthInfo = monthlist.find((m) => m.id === currentMonth);
      if (monthInfo) {
        // Find data for this specific month and year in the array
        // byMonthDetailed is an array: [{month: 1, year: 2026, emp: 29, worker: 104, ...}, ...]
        const monthDataItem = byMonthDetailed.find(
          (item) => item.month === currentMonth && item.year === currentYear,
        );

        monthLabels.push(`${monthInfo.month} ${currentYear}`);

        // Use API values when present; otherwise keep chart continuity with zeros.
        monthData.push({
          emp: monthDataItem?.emp || 0,
          work: monthDataItem?.worker || 0,
          ltifrEmployees: monthDataItem?.ltifrEmployees || 0,
          ltifrWorkers: monthDataItem?.ltifrWorkers || 0,
        });
      }

      // Move to next month
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    return { monthLabels, monthData };
  }, [
    fromDate,
    toDate,
    socialData?.totalWorkforce?.data?.monthly?.byMonthDetailed,
  ]);

  // Get gender diversity percentages (average across selected monthly range)
  const genderDiversityData = useMemo(() => {
    const byMonthDetailed =
      socialData?.totalWorkforce?.data?.monthly?.byMonthDetailed || [];
    const startCode = getPeriodCode(fromDate?.year, fromDate?.month);
    const endCode = getPeriodCode(toDate?.year, toDate?.month);
    const rowsInRange = byMonthDetailed.filter((row) => {
      const code = getPeriodCode(row?.year, row?.month);
      return code >= startCode && code <= endCode;
    });

    if (rowsInRange.length > 0) {
      return {
        malePercentage: getAverage(
          rowsInRange.map(
            (row) => Number(row?.genderDiversity?.malePercentage) || 0,
          ),
        ),
        femalePercentage: getAverage(
          rowsInRange.map(
            (row) => Number(row?.genderDiversity?.femalePercentage) || 0,
          ),
        ),
      };
    }

    return {
      malePercentage: socialData?.facility?.genderDiversity?.male ?? 70,
      femalePercentage: socialData?.facility?.genderDiversity?.female ?? 20,
    };
  }, [
    socialData?.totalWorkforce?.data?.monthly?.byMonthDetailed,
    socialData?.facility?.genderDiversity,
    fromDate,
    toDate,
  ]);

  const workforceBarChartData = [
    {
      label: "Emp",
      data: monthlyWorkforceData?.monthData?.map((item) => item.emp) || [],
      backgroundColor: "#007BFF",
      borderColor: "#007BFF",
      borderWidth: 0,
      barThickness: 20, // Small bars
      maxBarThickness: 20,
      marginTop: "-50px",
    },
    {
      label: "Work",
      data: monthlyWorkforceData?.monthData?.map((item) => item.work) || [],
      backgroundColor: "#FF4C4C",
      borderColor: "#FF4C4C",
      borderWidth: 0,
      barThickness: 20, // Small bars
      maxBarThickness: 20,
      marginTop: "10px",
    },
  ];
  const hasWorkforceBarData = useMemo(
    () =>
      Array.isArray(monthlyWorkforceData?.monthData) &&
      monthlyWorkforceData.monthData.length > 0,
    [monthlyWorkforceData?.monthData],
  );

  const lostTimeInjuryMonthlyData =
    socialData?.facility?.lostTimeInjuryMonthly || [];

  // Create monthly labels (Jan to Dec)
  const monthlyLabels = monthlist.map((m) => m.month);

  // Prepare line chart data for Lost Time Injury
  const lostTimeInjuryLineChartData = useMemo(() => {
    // If we have monthly data from API, use it; otherwise create sample data
    if (lostTimeInjuryMonthlyData.length > 0) {
      return [
        {
          label: "Lost Time Injury Frequency Rate",
          data: lostTimeInjuryMonthlyData.map((item) => item.value || 0),
          borderColor: "#007BFF",
          backgroundColor: "rgba(0, 123, 255, 0.1)",
          borderWidth: 2,
          fill: false,
          tension: 0.4, // Smooth curve
        },
      ];
    }
    // else {
    //   // Sample data pattern similar to image: starts low, peaks mid-year, dips, then rises
    //   const sampleData = [
    //     45000,  // Jan
    //     55000,  // Feb
    //     80000,  // Mar
    //     120000, // Apr (peak)
    //     115000, // May
    //     95000,  // Jun
    //     85000,  // Jul
    //     75000,  // Aug (trough)
    //     80000,  // Sep
    //     100000, // Oct
    //     150000, // Nov
    //     220000, // Dec (sharp rise)
    //   ];
    //   return [
    //     {
    //       label: "Lost Time Injury Frequency Rate",
    //       data: sampleData,
    //       borderColor: "#007BFF",
    //       backgroundColor: "rgba(0, 123, 255, 0.1)",
    //       borderWidth: 2,
    //       fill: false,
    //       tension: 0.4,
    //     },
    //   ];
    // }
  }, [lostTimeInjuryMonthlyData]);

  // Prepare line chart data for Emp and Worker using LTIFR values
  const empWorkerLineChartData = useMemo(() => {
    if (
      monthlyWorkforceData?.monthData &&
      monthlyWorkforceData.monthData.length > 0
    ) {
      // Start from actual month values (do not prepend 0 point).
      const empData = monthlyWorkforceData.monthData.map(
        (item) => parseFloat(item.ltifrEmployees) || 0,
      );
      const workData = monthlyWorkforceData.monthData.map(
        (item) => parseFloat(item.ltifrWorkers) || 0,
      );

      const pointRadiusArray = Array(monthlyWorkforceData.monthData.length).fill(
        5,
      );

      return [
        {
          label: "Emp",
          data: empData,
          borderColor: "#007BFF",
          backgroundColor: "rgba(0, 123, 255, 0.1)",
          borderWidth: 2,
        
          fill: false,
          tension: 0.4, // Smooth curve
          pointRadius: pointRadiusArray, // Hide first point at y-axis edge, show others
          pointBackgroundColor: "#007BFF",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          pointHoverRadius: 6,
        },
        {
          label: "Work",
          data: workData,
          borderColor: "#FF4C4C",
          backgroundColor: "rgba(255, 76, 76, 0.1)",
          borderWidth: 2,
          fill: false,
          tension: 0.4, // Smooth curve
          pointRadius: pointRadiusArray, // Hide first point at y-axis edge, show others
          pointBackgroundColor: "#FF4C4C",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          pointHoverRadius: 6,
        },
      ];
    }
    return [];
  }, [monthlyWorkforceData]);
  const hasEmpWorkerLineData = useMemo(
    () =>
      Array.isArray(empWorkerLineChartData) &&
      empWorkerLineChartData.length > 0,
    [empWorkerLineChartData],
  );

  const fetchSocialData = async () => {
    try {
      setIsLoadingWorkforce(true);
      let endpoints = {};

      if (viewMode === "monthly") {
        // Monthly API: send range filters for From/To
        const startMonthParam = String(fromDate.month || 1).padStart(2, "0");
        const startYearParam = fromDate.year || currentYear;
        const endMonthParam = String(toDate.month || currentMonth).padStart(
          2,
          "0",
        );
        const endYearParam = toDate.year || currentYear;
        const monthlyParams = `startMonth=${startMonthParam}&startYear=${startYearParam}&endMonth=${endMonthParam}&endYear=${endYearParam}&companyId=${selectedCompany?._id}`;

        endpoints = {
          facility: `social/dashboard/facilityCulture?${monthlyParams}`,
          labour: `social/dashboard/labourHealthAndSafety?${monthlyParams}`,
          totalWorkforce: `social/dashboard/totalWorkforce?${monthlyParams}`,
          humanRights: `social/dashboard/humanRightsIncident?${monthlyParams}`,
          training: `social/dashboard/trainingDevlopmentEmployeeBenefits?${monthlyParams}`,
          customerSatisfaction: `social/dashboard/customerSatisfaction?${monthlyParams}`,
        };
      } else {
        // Annual API: send year range for From/To
        const startYearParam = fromDate.year || currentYear;
        const endYearParam = toDate.year || currentYear;
        const annualParams = `startYear=${startYearParam}&endYear=${endYearParam}&companyId=${selectedCompany?._id}`;

        endpoints = {
          facility: `social/dashboard/facilityCulture?${annualParams}`,
          labour: `social/dashboard/labourHealthAndSafety?${annualParams}`,
          // Annual Total Workforce API (separate endpoint, pass year)
          totalWorkforce: `social/dashboard/totalWorkforce/annual?${annualParams}`,
          humanRights: `social/dashboard/humanRightsIncident?${annualParams}`,
          training: `social/dashboard/trainingDevlopmentEmployeeBenefits?${annualParams}`,
          customerSatisfaction: `social/dashboard/customerSatisfaction?${annualParams}`,
        };
      }
      const responses = await Promise.all(
        Object.entries(endpoints).map(async ([key, url]) => {
          const res = await get(url);
          // For training endpoint, include root-level properties like skillTrainingsData and awarenessTrainingsData
          // if (key === "training") {
          //   const socialDataRefs = res?.data?.socialDataRefs || {};
          //   const skillTrainingsData =
          //     res?.skillTrainingsData ||
          //     res?.data?.skillTrainingsData ||
          //     socialDataRefs?.skillTrainingsData ||
          //     socialDataRefs?.skillTrainings ||
          //     [];
          //   const awarenessTrainingsData =
          //     res?.awarenessTrainingsData ||
          //     res?.data?.awarenessTrainingsData ||
          //     socialDataRefs?.awarenessTrainingsData ||
          //     socialDataRefs?.awarenessTrainings ||
          //     [];

          //   return {
          //     [key]: {
          //       ...res.data,
          //       skillTrainingsData,
          //       awarenessTrainingsData,
          //     }
          //   };
          // }
          return { [key]: res.data };
        }),
      );
      const combinedData = responses.reduce(
        (acc, data) => ({ ...acc, ...data }),
        {},
      );

      // Enrich totalWorkforce with skill / awareness training data
      // and community engagement totals (e.g. totalDonated.amount)
      try {
        const totalWorkforceData = combinedData?.totalWorkforce || {};

        let skillTrainingsData = totalWorkforceData?.skillTrainingsData || [];
        let awarenessTrainingsData =
          totalWorkforceData?.awarenessTrainingsData || [];
        let communityEngagement = totalWorkforceData?.communityEngagement || {};
        let budgetAllocated = totalWorkforceData?.budgetAllocated || null;

        // If dashboard API didn't populate these, fall back to socialDataRefs
        const refs = totalWorkforceData?.socialDataRefs;
        if (
          Array.isArray(refs) &&
          refs.length > 0 &&
          (!skillTrainingsData.length ||
            !awarenessTrainingsData.length ||
            !Object.keys(communityEngagement).length)
        ) {
          const startCode = getPeriodCode(fromDate?.year, fromDate?.month);
          const endCode = getPeriodCode(toDate?.year, toDate?.month);

          const refsInRange = refs.filter((r) => {
            const md = r?.monthlyData || {};
            const monthVal =
              Number(md?.monthToSubmit) ||
              Number(r?.monthToSubmit) ||
              Number(md?.month) ||
              Number(r?.month);
            const yearVal =
              Number(md?.yearToSubmit) ||
              Number(r?.yearToSubmit) ||
              Number(md?.year) ||
              Number(r?.year);

            if (!monthVal || !yearVal) return false;
            const code = getPeriodCode(yearVal, monthVal);
            return code >= startCode && code <= endCode;
          });

          const trainingRows = (refsInRange.length ? refsInRange : refs).filter(
            (r) =>
              r?.monthlyData?.trainingAndDevelopment ||
              r?.monthlyData?.TrainingAndDevelopment,
          );

          const toRows = (arr = []) =>
            arr.map((item) => ({
              topic: item?.topic || "Topic",
              percentage:
                item?.percentage !== undefined && item?.percentage !== null
                  ? Number(item.percentage) || 0
                  : 0,
            }));

          const averageByTopic = (rows = []) => {
            const bucket = {};
            rows.forEach((row) => {
              const key = row?.topic || "Topic";
              if (!bucket[key]) bucket[key] = { topic: key, sum: 0, count: 0 };
              bucket[key].sum += Number(row?.percentage) || 0;
              bucket[key].count += 1;
            });
            return Object.values(bucket).map((x) => ({
              topic: x.topic,
              percentage: x.count > 0 ? x.sum / x.count : 0,
            }));
          };

          if (!skillTrainingsData.length) {
            const skillRows = trainingRows.flatMap((r) => {
              const tAndD =
                r?.monthlyData?.trainingAndDevelopment ||
                r?.monthlyData?.TrainingAndDevelopment ||
                {};
              if (Array.isArray(tAndD?.skillTrainings?.trainings)) {
                return toRows(tAndD.skillTrainings.trainings);
              }
              if (Array.isArray(tAndD?.skillTrainings)) {
                return toRows(tAndD.skillTrainings);
              }
              return [];
            });
            skillTrainingsData = averageByTopic(skillRows);
          }

          if (!awarenessTrainingsData.length) {
            const awarenessRows = trainingRows.flatMap((r) => {
              const tAndD =
                r?.monthlyData?.trainingAndDevelopment ||
                r?.monthlyData?.TrainingAndDevelopment ||
                {};
              if (Array.isArray(tAndD?.awarenessTrainings?.trainings)) {
                return toRows(tAndD.awarenessTrainings.trainings);
              }
              if (Array.isArray(tAndD?.awarenessTrainings)) {
                return toRows(tAndD.awarenessTrainings);
              }
              return [];
            });
            awarenessTrainingsData = averageByTopic(awarenessRows);
          }

          const firstWithCommunity = (
            refsInRange.length ? refsInRange : refs
          ).find((r) => r?.monthlyData?.communityEngagement);
          if (!Object.keys(communityEngagement).length && firstWithCommunity) {
            communityEngagement =
              firstWithCommunity.monthlyData.communityEngagement;
          }

          const firstWithBudget = trainingRows.find((r) => {
            const tAndD =
              r?.monthlyData?.trainingAndDevelopment ||
              r?.monthlyData?.TrainingAndDevelopment ||
              {};
            return Boolean(tAndD?.budgetAllocated);
          });
          if (!budgetAllocated && firstWithBudget) {
            const tAndD =
              firstWithBudget?.monthlyData?.trainingAndDevelopment ||
              firstWithBudget?.monthlyData?.TrainingAndDevelopment ||
              {};
            budgetAllocated = tAndD?.budgetAllocated || null;
          }
        }

        combinedData.totalWorkforce = {
          ...totalWorkforceData,
          skillTrainingsData,
          awarenessTrainingsData,
          communityEngagement,
          budgetAllocated,
        };
      } catch (e) {
        console.error("Failed to map training data into totalWorkforce", e);
      }

      setSocialData(combinedData);
      setIsLoadingWorkforce(false);
    } catch (error) {
      console.error("Error fetching governance data:", error);
      setIsLoadingWorkforce(false);
    }
  };

  useEffect(() => {
    if (!selectedCompany?._id) {
      return;
    }
    fetchSocialData();
  }, [viewMode, fromDate, toDate, user?._id, selectedCompany]);

  return (
    <MainDashboardLayout
      sidebar={user?.groupManager ? <GroupAdminSidebar /> : <SocialLayout />}
    >
      <Unauthorized requiredModule="Social">
        <div style={{ marginBottom: "50px" }}>
          <div className={styles.heading_container}>
            <div className={styles.heading_left}>
              <p className={styles.heading}>Social Dashboard</p>
              <div className={styles.view_toggle_container}>
                <button
                  type="button"
                  className={`${styles.view_toggle_button} ${
                    viewMode === "monthly"
                      ? styles.view_toggle_button_active
                      : ""
                  }`}
                  onClick={() => {
                    setViewMode("monthly");
                    setFromDate({ month: 1, year: currentYear });
                    setToDate({ month: currentMonth, year: currentYear });
                  }}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`${styles.view_toggle_button} ${
                    viewMode === "annual"
                      ? styles.view_toggle_button_active
                      : ""
                  }`}
                  onClick={() => {
                    setViewMode("annual");
                    setFromDate({ month: 1, year: currentYear });
                    setToDate({ month: 12, year: currentYear });
                  }}
                >
                  Annually
                </button>
              </div>
            </div>
            <div className={styles.filter_container}>
              {viewMode === "monthly" ? (
                <>
                  <div>
                    <p className={styles.filter_label}>From</p>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <select
                        name="fromMonth"
                        id="fromMonth"
                        value={fromDate.month}
                        onChange={(e) => {
                          const nextMonth = Number(e.target.value);
                          setFromDate((prev) => ({
                            ...prev,
                            month: nextMonth,
                          }));
                          setToDate((prev) => {
                            if (
                              Number(prev.year) === Number(fromDate.year) &&
                              Number(prev.month) < nextMonth
                            ) {
                              return { ...prev, month: nextMonth };
                            }
                            return prev;
                          });
                        }}
                        className={styles.heading_select_month}
                      >
                        {fromMonthOptions.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.month}
                          </option>
                        ))}
                      </select>
                      <select
                        name="fromYear"
                        id="fromYear"
                        value={fromDate.year}
                        onChange={(e) => {
                          const nextYear = Number(e.target.value);
                          setFromDate((prev) => ({ ...prev, year: nextYear }));
                          setToDate((prev) => {
                            if (Number(prev.year) < nextYear) {
                              return { ...prev, year: nextYear };
                            }
                            if (
                              Number(prev.year) === nextYear &&
                              Number(prev.month) < Number(fromDate.month)
                            ) {
                              return { ...prev, month: Number(fromDate.month) };
                            }
                            return prev;
                          });
                        }}
                        className={styles.heading_select_year}
                      >
                        {yearList.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <p className={styles.filter_label}>To</p>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <select
                        name="toMonth"
                        id="toMonth"
                        value={toDate.month}
                        onChange={(e) =>
                          setToDate((prev) => ({
                            ...prev,
                            month: Number(e.target.value),
                          }))
                        }
                        className={styles.heading_select_month}
                      >
                        {toMonthOptions.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.month}
                          </option>
                        ))}
                      </select>
                      <select
                        name="toYear"
                        id="toYear"
                        value={toDate.year}
                        onChange={(e) => {
                          const nextYear = Number(e.target.value);
                          setToDate((prev) => ({ ...prev, year: nextYear }));
                        }}
                        className={styles.heading_select_year}
                      >
                        {toYearOptions.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className={styles.filter_label}>From</p>
                    <select
                      name="annualFromYear"
                      id="annualFromYear"
                      value={fromDate.year}
                      onChange={(e) => {
                        const nextYear = Number(e.target.value);
                        setFromDate({ month: 1, year: nextYear });
                        setToDate((prev) => ({
                          month: 12,
                          year:
                            Number(prev.year) < nextYear ? nextYear : prev.year,
                        }));
                      }}
                      className={styles.heading_select_year}
                    >
                      {yearList.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <p className={styles.filter_label}>To</p>
                    <select
                      name="annualToYear"
                      id="annualToYear"
                      value={toDate.year}
                      onChange={(e) =>
                        setToDate({ month: 12, year: Number(e.target.value) })
                      }
                      className={styles.heading_select_year}
                    >
                      {toYearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
          {viewMode === "annual" && (
            <>
              <div className={styles.facility_container}>
                <p className={styles.heading_sub}>Employee Wellbeing</p>
                {/* <select
              name="facility"
              id="facility"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className={styles.heading_select_month}
            >
              <option value="facility1">Facility 1</option>
              <option value="facility2">Facility 2</option>
              <option value="facility3">Facility 3</option>
            </select> */}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "1.2rem",
                  marginTop: "1.5rem",
                  width: "100%",
                }}
              >
                <div className={styles.diversity_container}>
                  <p className={styles.diversity_heading}>
                    Permanent Employee Turnover Rate
                  </p>
                  <div
                    className={styles.diversity_value}
                    style={{
                      marginTop: "1rem",
                      fontWeight: 400,
                      fontSize: "16px",
                    }}
                  >
                    Male:{" "}
                    {truncateNumber(
                      employeeWellBeingSummary?.turnoverRatePermEmpMale ?? 0,
                      2,
                    )}
                  </div>
                  <div
                    className={styles.diversity_value}
                    style={{
                      marginTop: "0.5rem",
                      fontWeight: 400,
                      fontSize: "16px",
                    }}
                  >
                    Female:{" "}
                    {truncateNumber(
                      employeeWellBeingSummary?.turnoverRatePermEmpFemale ?? 0,
                      2,
                    )}
                  </div>
                </div>

                <div className={styles.diversity_container}>
                  <p className={styles.diversity_heading}>
                    Permanent Worker Turnover Rate
                  </p>
                  <div
                    className={styles.diversity_value}
                    style={{
                      marginTop: "1rem",
                      fontWeight: 400,
                      fontSize: "16px",
                    }}
                  >
                    Male:{" "}
                    {truncateNumber(
                      employeeWellBeingSummary?.turnoverRatePermWorkerMale ?? 0,
                      2,
                    )}
                  </div>
                  <div
                    className={styles.diversity_value}
                    style={{
                      marginTop: "0.5rem",
                      fontWeight: 400,
                      fontSize: "16px",
                    }}
                  >
                    Female:{" "}
                    {truncateNumber(
                      employeeWellBeingSummary?.turnoverRatePermWorkerFemale ??
                        0,
                      2,
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {viewMode === "monthly" && (
            <>
              <p className={styles.heading_sub} style={{ marginTop: "1.5rem" }}>
                Employee Wellbeing
              </p>
              <div className={styles.cards_container_two}>
                {/* <Cards
              title={"Gender Diversity Ratio "}
              subHeading={""}
              value={truncateNumber(
                socialData?.facility?.genderDiversity?.value ?? 0,
                2
              )}
              changeValue={`${truncateNumber(
                socialData?.facility?.genderDiversity?.percentage ?? 0,
                2
              )}% YOY`}
              change={getChangeType(
                socialData?.facility?.genderDiversity?.percentage
              )}
            /> */}

                <div className={styles.diversity_container}>
                  <p className={styles.diversity_heading}>Total Workforce</p>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                      // padding: "1rem 0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: "bold",
                        color: "#001632",
                      }}
                    >
                      {/* {formatNumberCommaSeparated(totalWorkforce * 10000)} */}
                    </div>
                    <div style={{ height: "250px", width: "100%" }}>
                      {isLoadingWorkforce ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "250px",
                            width: "100%",
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : hasWorkforceBarData ? (
                        <BarChart
                          labels={monthlyWorkforceData?.monthLabels || []}
                          datasets={workforceBarChartData}
                          yAxisTitle="Workforce"
                          customHeight="250px"
                          display={true}
                        />
                      ) : (
                        renderNoData("250px")
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.diversity_container}>
                  <p className={styles.diversity_heading}>
                    Lost Time Injury Frequency Rate
                  </p>
                  <div
                    style={{
                      height: "250px",
                      width: "100%",
                      // marginTop: "1rem"
                    }}
                  >
                    {isLoadingWorkforce ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "250px",
                          width: "100%",
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : hasEmpWorkerLineData ? (
                      <LineChart
                        dataset={empWorkerLineChartData}
                        labels={monthlyWorkforceData?.monthLabels || []}
                        yAxisTitle="LTIFR"
                        customHeight="250px"
                        display={true}
                        legendPointSize={9}
                      />
                    ) : (
                      renderNoData("250px")
                    )}
                  </div>
                </div>
              </div>
              {/* Gender Diversity Card - Text List */}
              <p className={styles.heading_sub}>Diversity and Inclusion</p>
              <div className={styles.cards_container_two}>
                <div className={styles.diversity_container}>
                  <p className={styles.diversity_heading}>Gender Diversity</p>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                      padding: "1rem 0",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "14px", color: "#001632" }}>
                        Male
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#001632",
                        }}
                      >
                        {truncateNumber(genderDiversityData.malePercentage, 2)}%
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "14px", color: "#001632" }}>
                        Female
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#001632",
                        }}
                      >
                        {truncateNumber(
                          genderDiversityData.femalePercentage,
                          2,
                        )}
                        %
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.diversity_container}>
                  <p className={styles.diversity_heading}>
                    Diversity in Leadership
                  </p>
                  <div className={styles.diversity_pie_chart}>
                    <div className={styles.table_div}>
                      {labels.map((item, index) => (
                        <div key={index} className={styles.table_container}>
                          <div className={styles.table_row}>
                            <span
                              className={styles.table_row_label}
                              style={{
                                backgroundColor: item.color,
                              }}
                            ></span>
                            <span className={styles.table_text}>
                              {item.label}
                            </span>
                          </div>
                          <span className={styles.table_text}>
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                    {Array.isArray(diversityInLeadershipData?.labels) &&
                    diversityInLeadershipData.labels.length > 0 ? (
                      <PieChart
                        labels={
                          diversityInLeadershipData?.labels || [
                            "Male",
                            "Female",
                          ]
                        }
                        dataset={customDatasets}
                        customWidth={100}
                        customHeight={100}
                      />
                    ) : (
                      renderNoData("100px")
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Community Engagement Section */}
          {viewMode === "monthly" && (
            <>
              <div className={styles.facility_container}>
                <p className={styles.heading_sub}>Community Engagement</p>
              </div>
              <div className={styles.cards_container_one}>
                <Cards
                  title={"Community Spend"}
                  subHeading={""}
                  value={
                    <span style={{ fontSize: "15px", display: "block" }}>
                      {String(communitySpendDisplay || "")
                        .split(" , ")
                        .map((entry, idx) => (
                          <span
                            key={`${entry}-${idx}`}
                            style={{ display: "block" }}
                          >
                            {entry}
                          </span>
                        ))}
                    </span>
                  }
                  // changeValue={`${truncateNumber(
                  //   socialData?.training?.communitySpend?.percentage ?? 0,
                  //   0
                  // )}% YOY`}
                  // change={getChangeType(
                  //   socialData?.training?.communitySpend?.percentage ?? 0
                  // )}
                />
                <Cards
                  title={"Volunteer Hours"}
                  subHeading={""}
                  value={truncateNumber(volunteerHoursAverage ?? 0, 0)}
                  // changeValue={`${truncateNumber(
                  //   socialData?.totalWorkforce?.communityPercentage ?? 0,
                  //   0
                  // )}% YOY`}
                  // change={getChangeType(
                  //   socialData?.totalWorkforce?.communityPercentage ?? 0
                  // )}
                />
              </div>

              <div className={styles.cards_container_three}>
                <div className={styles.labour_container}>
                  <p className={styles.heading_sub}>Labour Practices</p>
                  <div style={{ display: "flex", gap: "1.2rem", width: "66%" }}>
                    <div className={styles.diversity_container}>
                      <p className={styles.diversity_heading}>
                        {`Complaint Resolution Rate:  ${
                          truncateNumber(
                            labourPracticesSummary?.complaintRate ?? 0,
                            2,
                          ) ?? 0
                        }%`}
                      </p>
                      <div className={styles.complaint_div}>
                        <div>
                          <p className={styles.complaint_heading}>
                            Total Received
                          </p>
                          <p className={styles.complaint_total}>
                            {truncateNumber(
                              labourPracticesSummary?.labourComplaints ?? 0,
                              2,
                            )}
                          </p>
                        </div>
                        <div>
                          <p className={styles.complaint_heading}>
                            Total Resolved
                          </p>
                          <p className={styles.complaint_resolved}>
                            {truncateNumber(
                              labourPracticesSummary?.labourComplaintsResolved ??
                                0,
                              2,
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className={styles.diversity_container}>
                      <p className={styles.diversity_heading}>POSH Cases</p>
                      <div
                        className={styles.complaint_div}
                        style={{ gap: "6rem" }}
                      >
                        <div>
                          <p className={styles.complaint_heading}>
                            Total
                            <br /> Received
                          </p>
                          <p className={styles.complaint_total}>
                            {truncateNumber(
                              labourPracticesSummary?.poshResolved?? 0,
                              0,
                            )}
                          </p>
                        </div>
                        <div>
                          <p className={styles.complaint_heading}>
                            Total <br /> Pending
                          </p>
                          <p className={styles.complaint_resolved}>
                            {truncateNumber(
                              labourPracticesSummary?.poshPending ?? 0,
                              0,
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "1.2rem",
                  width: "75%",
                  flex: "1 1 calc(50% - 1rem)",
                }}
              >
                <div className={styles.health_container}>
                  <p className={styles.heading_sub}>Health & Safety</p>
                  <div
                    className={styles.diversity_container}
                    style={{ flex: "1 1 calc(50% - 1rem)" }}
                  >
                    <p className={styles.diversity_heading}>Incident Count</p>
                    <div className={styles.diversity_value}>
                      {truncateNumber(
                        healthAndSafetySummary?.incidentCount ?? 0,
                        0,
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.health_container}>
                  <p className={styles.heading_sub}>Human Rights</p>
                  <div
                    className={styles.diversity_container}
                    style={{ flex: "1 1 calc(50% - 1rem)" }}
                  >
                    <p className={styles.diversity_heading}>
                      Resolution Rate of Human Rights Incidents
                    </p>
                    <div
                      className={styles.diversity_value}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {truncateNumber(
                          humanRightsSummary?.incidentsResolutionRate ?? 0,
                          0,
                        )}
                        %
                      </span>
                      {/* <span>
                        {truncateNumber(
                          Math.abs(
                            socialData?.totalWorkforce?.humnaRightPercentage ?? 0
                          ),
                          0
                        )}
                        % YOY
                      </span> */}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.cards_container_four}>
                <div
                  className={styles.training_container}
                  style={{ width: "50%" }}
                >
                  <p className={styles.heading_sub}>Training and Development</p>
                  <div className={styles.diversity_container}>
                    <p className={styles.diversity_heading}>
                      Budget allocated{" "}
                    </p>
                    <div className={styles.diversity_value}>
                      {trainingBudgetDisplay}
                    </div>
                  </div>
                </div>
              </div>

              {/* Skill Training and Awareness Training Charts */}
              <div
                style={{
                  // display: "flex",
                  gap: "1.2rem",
                  marginTop: "1.5rem",
                  width: "100%",
                }}
              >
                {/* Budget Allocated Input Field */}

                {/* Skill Training Chart */}
                <div
                  className={styles.diversity_container}
                  style={{ width: "100%" }}
                >
                  <p className={styles.diversity_heading}>Skill Training</p>
                  <div
                    style={{
                      width: "100%",
                      marginTop: "1rem",
                      display: "flex",
                      flexDirection: "column",
                     
                    }}
                  >
                    {Array.isArray(
                      socialData?.totalWorkforce?.skillTrainingsData,
                    ) &&
                    socialData.totalWorkforce.skillTrainingsData.length > 0 ? (
                      <>
                        <BarChart
                          labels={skillTrainingAlphaLabels}
                          datasets={[
                            {
                              label: "Employee Participation Rate (%)",
                              data:
                                socialData?.totalWorkforce?.skillTrainingsData?.map(
                                  (item) => parseFloat(item.percentage) || 0,
                                ) || [],
                              backgroundColor: "#007BFF",
                              borderColor: "#007BFF",
                              borderWidth: 0,
                            },
                          ]}
                          yAxisTitle="Employee Participation Rate (%)"
                          customHeight="300px"
                          display={false}
                        />
                        <div
                          style={{
                            marginTop: "0.75rem",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.5rem 1rem",
                             justifyContent: "center"
                          }}
                        >
                          {skillTrainingTopics.map((name, idx) => (
                            <div
                              key={`skill-legend-${idx}`}
                              style={{
                                fontSize: "12px",
                                overflowWrap: "anywhere",
                                // flex: "0 0 calc(33.333% - 0.67rem)",
                              }}
                            >
                              <strong>{toAlphaLabel(idx)}:</strong> {name}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      renderNoData("300px")
                    )}
                  </div>
                </div>

                {/* Awareness Training Chart */}
                <div
                  className={styles.diversity_container}
                  style={{ width: "100%", marginTop: "1.5rem" }}
                >
                  <p className={styles.diversity_heading}>Awareness Training</p>
                  <div
                    style={{
                      width: "100%",
                      marginTop: "1rem",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {Array.isArray(averagedAwarenessTrainingsData) &&
                    averagedAwarenessTrainingsData.length > 0 ? (
                      <>
                        <BarChart
                          labels={awarenessTrainingAlphaLabels}
                          datasets={[
                            {
                              label: "Employee Participation Rate (%)",
                              data:
                                averagedAwarenessTrainingsData?.map(
                                  (item) => parseFloat(item.percentage) || 0,
                                ) || [],
                              backgroundColor: "#007BFF",
                              borderColor: "#007BFF",
                              borderWidth: 0,
                            },
                          ]}
                          yAxisTitle="Employee Participation Rate (%)"
                          customHeight="300px"
                          display={false}
                        />
                        <div
                          style={{
                            marginTop: "0.75rem",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.5rem 1rem",
                            justifyContent: "center"
                          }}
                        >
                          {awarenessTrainingTopics.map((name, idx) => (
                            <div
                              key={`awareness-legend-${idx}`}
                              style={{
                                fontSize: "12px",
                                overflowWrap: "anywhere",
                                // flex: "0 0 calc(33.333% - 0.67rem)",
                              }}
                            >
                              <strong>{toAlphaLabel(idx)}:</strong> {name}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      renderNoData("300px")
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.customer_container}>
                <p className={styles.heading_sub}>Customer Satisfaction</p>
                <div
                  style={{
                    display: "flex",
                    gap: "1.2rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    className={styles.diversity_container}
                    style={{
                      flex: "1 1 calc(33.333% - 1rem)",
                      minWidth: "200px",
                    }}
                  >
                    <p className={styles.diversity_heading}>Data Breach Rate</p>
                    <div className={styles.diversity_value}>
                      {truncateNumber(
                        customerSatisfactionSummary?.dataBreaches ?? 0,
                        2,
                      )}
                    </div>
                  </div>
                  <div
                    className={styles.diversity_container}
                    style={{
                      flex: "1 1 calc(33.333% - 1rem)",
                      minWidth: "200px",
                    }}
                  >
                    <p className={styles.diversity_heading}>
                      Product Recall Rate
                    </p>
                    <div className={styles.diversity_value}>
                      {truncateNumber(
                        customerSatisfactionSummary?.productRecalls ?? 0,
                        2,
                      )}
                    </div>
                  </div>
                  <div
                    className={styles.diversity_container}
                    style={{
                      flex: "1 1 calc(33.333% - 1rem)",
                      minWidth: "200px",
                    }}
                  >
                    <p className={styles.diversity_heading}>
                      Marketing complaints
                    </p>
                    <div className={styles.diversity_value}>
                      {truncateNumber(
                        customerSatisfactionSummary?.marketingComplaints ?? 0,
                        0,
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Section - Policy Coverage % (intentionally omitted) */}
            </>
          )}

          {/* Annual-only: end section with 2 cards (Labour Practices + Additional) */}
          {viewMode === "annual" && (
            <div style={{ marginTop: "1.5rem" }}>
              <div style={{ display: "flex", gap: "1.2rem", width: "100%" }}>
                {/* Labour Practices card */}
                <div className={styles.diversity_container} style={{ flex: 1 }}>
                  <p className={styles.heading_sub}>Labour Practices</p>
                  <p className={styles.additional_heading}>
                    Assessment of the year
                  </p>
                  {(() => {
                    const labels = annualAssessmentSummary?.labels || [];
                    const values = annualAssessmentSummary?.values || [];

                    if (!labels.length) {
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "200px",
                          }}
                        >
                          <span style={{ color: "#d3d3d3", fontSize: "14px" }}>
                            No assessment data available
                          </span>
                        </div>
                      );
                    }

                    const baseColors = [
                      "#FF4C4C",
                      "#007BFF",
                      "#FFB020",
                      "#FFC94A",
                      "#FFE07A",
                    ];
                    const colors = labels.map(
                      (_, idx) => baseColors[idx % baseColors.length],
                    );

                    const data = {
                      labels,
                      datasets: [
                        {
                          data: values,
                          backgroundColor: colors,
                          borderColor: "#fff",
                          borderWidth: 0,
                        },
                      ],
                    };

                    const options = {
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: "55%",
                      plugins: {
                        legend: { display: false },
                        tooltip: { enabled: true },
                        datalabels: { display: false },
                      },
                    };

                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "2rem",
                        }}
                      >
                        <div style={{ width: "200px", height: "200px" }}>
                          {values.length > 0 ? (
                            <Doughnut data={data} options={options} />
                          ) : (
                            renderNoData("200px")
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.75rem",
                            flex: 1,
                          }}
                        >
                          {labels.map((label, idx) => (
                            <div
                              key={`${label}-${idx}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              <div
                                style={{
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor: colors[idx],
                                }}
                              />
                              <span
                                style={{ fontSize: "14px", color: "#001632" }}
                              >
                                {label}
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#001632",
                                  marginLeft: "auto",
                                }}
                              >
                                {truncateNumber(values[idx], 2)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Additional card */}
                <div className={styles.diversity_container} style={{ flex: 1 }}>
                  <p className={styles.heading_sub}>Additional</p>
                  <p className={styles.additional_heading}>Policy Coverage %</p>
                  {(() => {
                    const safeAvail =
                      annualPolicyCoverageSummary?.availableAvg || 0;
                    const safeTotal =
                      12;
                    const coveragePercent =
                      annualPolicyCoverageSummary?.coveragePercent || 0;
                    const remainingPercent =
                      annualPolicyCoverageSummary?.remainingPercent ?? 100;
                    const hasAny = coveragePercent > 0 || remainingPercent > 0;
                    const data = {
                      labels: [
                        "Available with company",
                        "Total Listed Policy Count",
                      ],
                      datasets: [
                        {
                          // Pie chart values in percentage, legend still shows raw counts
                          data: hasAny
                            ? [coveragePercent, 12]
                            : [20, 80],
                          backgroundColor: ["#007BFF", "#9C27B0"],
                          borderColor: "#fff",
                          borderWidth: 0,
                        },
                      ],
                    };

                    const options = {
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: "55%",
                      plugins: {
                        legend: { display: false },
                        tooltip: { enabled: true },
                        datalabels: { display: false },
                      },
                    };

                    // Legend shows count values (not percentage)
                    const legendAvail = hasAny ? safeAvail : 20;
                    const legendTotal = hasAny ? safeTotal : 80;

                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "2rem",
                        }}
                      >
                        <div style={{ width: "200px", height: "200px" }}>
                          {hasAny ? (
                            <Doughnut data={data} options={options} />
                          ) : (
                            renderNoData("200px")
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: "#007BFF",
                              }}
                            />
                            <span
                              style={{ fontSize: "14px", color: "#001632" }}
                            >
                              Available with company
                            </span>
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#001632",
                                marginLeft: "auto",
                              }}
                            >
                              {truncateNumber(legendAvail, 2)}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: "#9C27B0",
                              }}
                            />
                            <span
                              style={{ fontSize: "14px", color: "#001632" }}
                            >
                              Total Listed Policy Count
                            </span>
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#001632",
                                marginLeft: "auto",
                              }}
                            >
                              {truncateNumber(legendTotal, 2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              {/* Annual: Employee Benefits (matches image: 2 cards + 1 bar chart) */}
              {(() => {
                const labels = annualEmployeeBenefitsSummary?.labels?.length
                  ? annualEmployeeBenefitsSummary.labels
                  : Array.from({ length: 11 }, () => "");
                const alphaLabels = labels.map((_, idx) => toAlphaLabel(idx));
                const permanentByProgram = annualEmployeeBenefitsSummary
                  ?.permanentByProgram?.length
                  ? annualEmployeeBenefitsSummary.permanentByProgram
                  : [];
                const otherByProgram = annualEmployeeBenefitsSummary
                  ?.otherByProgram?.length
                  ? annualEmployeeBenefitsSummary.otherByProgram
                  : [];

                return (
                  <>
                    <div className={styles.facility_container}>
                      <p className={styles.heading_sub}>Employee Benefits</p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.2rem",
                        width: "100%",
                        marginTop: "1rem",
                      }}
                    >
                      <div
                        className={styles.diversity_container}
                        style={{ flex: 1, width: "50%" }}
                      >
                        <p className={styles.diversity_heading}>Total INR</p>
                        <div className={styles.diversity_value}>
                          {truncateNumber(
                            Number(annualBenefitsTotalInrAverage || 0),
                            0,
                          )}
                        </div>
                      </div>
                      {/* <div className={styles.diversity_container} style={{ flex: 1 }}>
                        <p className={styles.diversity_heading}>
                          No. of Permanant Employees benefited
                        </p>
                        <div className={styles.diversity_value}>
                          {truncateNumber(Number(permanentBenefitedTotal || 0), 0)}
                        </div>
                      </div> */}
                    </div>
                    <div
                      className={styles.diversity_container}
                      style={{ width: "100%", marginTop: "1.2rem" }}
                    >
                      <p className={styles.diversity_heading}>
                        No. of Permanant Employees benefited
                      </p>
                      <div style={{ width: "100%" }}>
                        {Array.isArray(permanentByProgram) &&
                        permanentByProgram.length > 0 ? (
                          <>
                            <BarChart
                              labels={alphaLabels}
                              datasets={[
                                {
                                  // label: "Employee Benefits Programs Offered",
                                  data: permanentByProgram,
                                  backgroundColor: "#007BFF",
                                  borderColor: "#007BFF",
                                  borderWidth: 0,
                                  barThickness: 20,
                                  maxBarThickness: 20,
                                },
                              ]}
                              yAxisTitle="Number of employees"
                              customHeight="260px"
                              display={false}
                            />
                            <div
                              style={{
                                marginTop: "0.75rem",
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "0.5rem 1rem",
                                justifyContent: "center",
                              }}
                            >
                              {labels.map((name, idx) => (
                                <div
                                  key={`perm-benefit-legend-${idx}`}
                                  style={{
                                    fontSize: "12px",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  <strong>{toAlphaLabel(idx)}:</strong> {name}
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div
                            style={{
                              height: "260px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#9aa0a6",
                              fontSize: "20px",
                              fontWeight: 600,
                            }}
                          >
                            No Data Found
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className={styles.diversity_container}
                      style={{ width: "100%", marginTop: "1.2rem" }}
                    >
                      <p className={styles.diversity_heading}>
                        No. of Other than Permanant Employees benefited
                      </p>
                      <div style={{ width: "100%" }}>
                        {Array.isArray(otherByProgram) &&
                        otherByProgram.length > 0 ? (
                          <>
                            <BarChart
                              labels={alphaLabels}
                              datasets={[
                                {
                                  label: "Employee Benefits Programs Offered",
                                  data: otherByProgram,
                                  backgroundColor: "#007BFF",
                                  borderColor: "#007BFF",
                                  borderWidth: 0,
                                  barThickness: 20,
                                  maxBarThickness: 20,
                                },
                              ]}
                              yAxisTitle="Number of employees"
                              customHeight="260px"
                              display={false}
                            />
                            <div
                              style={{
                                marginTop: "0.75rem",
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "0.5rem 1rem",
                                justifyContent: "center",
                              }}
                            >
                              {labels.map((name, idx) => (
                                <div
                                  key={`other-benefit-legend-${idx}`}
                                  style={{
                                    fontSize: "12px",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  <strong>{toAlphaLabel(idx)}:</strong> {name}
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          renderNoData("260px")
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}

              <div style={{ height: "1.2rem" }} />
            </div>
          )}
        </div>
      </Unauthorized>
    </MainDashboardLayout>
  );
};

export default SocialDashboard;
